import * as React from 'react';
import { ErrorState, PageHeader } from '@payment-plan/ui';
import { ApplicantsStep } from './components/ApplicantsStep';
import { ConditionsStep } from './components/ConditionsStep';
import { FlowSteps } from './components/FlowSteps';
import { InstallmentsStep } from './components/InstallmentsStep';
import type { CuotaInicial } from './components/InstallmentsStep';
import { UnitStep } from './components/UnitStep';
import { formatCOP, toPesos } from './money';
import { toPlanConditions } from './separation.schema';
import type { ApplicantsFormValues, ConditionsFormValues } from './separation.schema';
import { useAuth } from '../../auth/auth-context';
import { can } from '../../lib/permissions';
import type { FlowDraft, PaymentPlanPreview } from '../../types/separations';

export type Paso = 1 | 2 | 3 | 4;

/** Estado con el que arranca el recorrido: vacío (nueva) o con lo guardado (retomar/corregir, fix-50). */
export interface EstadoInicialFlujo {
  paso: Paso;
  draft: FlowDraft;
  separantes?: ApplicantsFormValues;
  condiciones?: ConditionsFormValues;
  /** Cuotas ya guardadas: se ofrecen en el paso 4 si cuadran con el plan recalculado. */
  cuotasGuardadas?: CuotaInicial[];
}

/**
 * Flujo del asesor (HU-WEB-005): unidad → separantes + autorización → condiciones y preview del back →
 * ajuste de cuotas → Generar PDF (feat-26). El estado del recorrido vive aquí; el servidor, en React Query.
 */
export function NewSeparationPage(): React.JSX.Element {
  const { user } = useAuth();

  if (!can(user?.permissions, 'separacion.crear')) {
    return (
      <ErrorState title="Acceso restringido" message="No tienes permiso para crear separaciones." />
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader
        eyebrow="Sala de ventas"
        title="Nueva separación"
        description="Selecciona la unidad, registra al separante y cuadra el plan de pagos."
      />
      <SeparationFlow />
    </div>
  );
}

/** El recorrido por pasos; lo usan la separación nueva y la edición de un borrador o una corrección. */
export function SeparationFlow({ inicial }: { inicial?: EstadoInicialFlujo }): React.JSX.Element {
  const [paso, setPaso] = React.useState<Paso>(inicial?.paso ?? 1);
  const [draft, setDraft] = React.useState<FlowDraft | null>(inicial?.draft ?? null);
  const [separantes, setSeparantes] = React.useState<ApplicantsFormValues | undefined>(inicial?.separantes);
  const [condiciones, setCondiciones] = React.useState<ConditionsFormValues | undefined>(inicial?.condiciones);
  const [plan, setPlan] = React.useState<PaymentPlanPreview | null>(null);

  // Las cuotas guardadas solo se ofrecen si suman exactamente el saldo del plan recalculado por el back.
  const cuotasIniciales =
    plan && inicial?.cuotasGuardadas?.length &&
    inicial.cuotasGuardadas.reduce((suma, c) => suma + toPesos(c.valor), 0n) === toPesos(plan.balanceToDefer)
      ? inicial.cuotasGuardadas
      : undefined;

  return (
    <>
      <FlowSteps current={paso} />

      {draft && paso > 1 && <DraftSummary draft={draft} />}

      {paso === 1 && (
        <UnitStep
          onCreated={(nuevo) => {
            setDraft(nuevo);
            setPaso(2);
          }}
        />
      )}

      {paso === 2 && draft && (
        <ApplicantsStep
          separacionId={draft.id}
          initialValues={separantes}
          onDone={(values) => {
            setSeparantes(values);
            setPaso(3);
          }}
        />
      )}

      {paso === 3 && draft && (
        <ConditionsStep
          draft={draft}
          initialValues={condiciones}
          initialPlan={plan}
          onBack={() => setPaso(2)}
          onContinue={(nuevoPlan, values) => {
            setPlan(nuevoPlan);
            setCondiciones(values);
            setPaso(4);
          }}
        />
      )}

      {paso === 4 && draft && plan && condiciones && (
        <InstallmentsStep
          separacionId={draft.id}
          plan={plan}
          condiciones={toPlanConditions(condiciones)}
          cuotasIniciales={cuotasIniciales}
          onBack={() => setPaso(3)}
        />
      )}
    </>
  );
}

function DraftSummary({ draft }: { draft: FlowDraft }): React.JSX.Element {
  const adicionales = draft.adicionales.map((u) => u.numero).join(', ');
  return (
    <section
      aria-label="Resumen de la separación"
      className="grid gap-3 rounded-xl border border-border/70 bg-surface-fade p-4 text-sm shadow-elegant sm:grid-cols-4"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Proyecto</p>
        <p className="font-medium text-foreground">{draft.proyecto.nombre}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Unidad</p>
        <p className="font-medium text-foreground">
          {draft.unidad.torre} · {draft.unidad.numero}
        </p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Adicionales</p>
        <p className="font-medium text-foreground">{adicionales || 'Ninguna'}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Precio de lista total</p>
        <p className="font-semibold tabular-nums text-foreground">{formatCOP(draft.precioTotal)}</p>
      </div>
    </section>
  );
}
